import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LANGUAGES, LANGUAGE_LABELS, type Language } from "@family/shared";
import { AppHeader } from "../components/AppHeader";
import { AvatarUploader } from "../components/AvatarUploader";
import { TreeSelfLink } from "../components/TreeSelfLink";
import { PlaceTypeahead } from "../components/PlaceTypeahead";
import { NotificationToggle } from "../components/NotificationToggle";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { apiFetch } from "../lib/api";
import { getFamilySlugForHost } from "../lib/families";
import type {
  BranchStatus,
  BranchesResponse,
  MeResponse,
  PlaceSuggestion,
  UpdateProfileRequest,
} from "@family/shared";

/**
 * The profile page does double duty: it's the always-available editor for
 * a user's own profile, AND it's the onboarding gate shown immediately
 * after first sign-in. The onboarding mode is signalled by ?onboarding=1
 * and shown when the user hasn't filled in firstName yet.
 */
export function ProfilePage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isOnboarding =
    searchParams.get("onboarding") === "1" ||
    (meQuery.data?.user.needsOnboarding ?? false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [bio, setBio] = useState("");
  const [languages, setLanguages] = useState<Language[]>([]);
  // Single combined location field. Either picked from typeahead (locked
  // lat/lng) or free text (geocoded best-effort by World map).
  const [placeText, setPlaceText] = useState("");
  const [pickedPlace, setPickedPlace] = useState<PlaceSuggestion | null>(null);
  const [placeWasPicked, setPlaceWasPicked] = useState(false);
  // Tree hints for moderators. Free text on purpose (see users.branch_*).
  const [branchStatus, setBranchStatus] = useState<BranchStatus | null>(null);
  const [branchPersonId, setBranchPersonId] = useState<string | null>(null);
  const [motherName, setMotherName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [knownRelative, setKnownRelative] = useState("");
  // Onboarding asks for the parents' names, so it needs an explicit escape
  // hatch — adopted, married in, or simply doesn't know. Ticking it clears
  // both fields and lifts the requirement; it is not stored anywhere.
  const [parentsUnknown, setParentsUnknown] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // A sign-up has no membership yet, so the family comes from the hostname
  // (same map the login page uses). Unknown host (localhost) = no picker.
  const familySlug = primary?.familySlug ?? getFamilySlugForHost();
  const branchesQuery = useQuery<BranchesResponse>({
    queryKey: ["branches", familySlug],
    queryFn: () => apiFetch<BranchesResponse>(`/families/${familySlug}/branches`),
    enabled: Boolean(familySlug),
  });

  useEffect(() => {
    if (!hydrated && meQuery.data) {
      const u = meQuery.data.user;
      // Phone sign-ups have display_name == the phone number (the token
      // carries no name), and splitting that into first/last pre-filled the
      // form with the number — one press of Continue and the family gets a
      // member called "+4670…". Only seed from a display name that is a name.
      if (!u.firstName && u.displayName && u.displayName !== u.phone) {
        const parts = u.displayName.trim().split(/\s+/);
        setFirstName(parts[0] ?? "");
        setLastName(parts.slice(1).join(" "));
      } else {
        setFirstName(u.firstName ?? "");
        setLastName(u.lastName ?? "");
      }
      setBio(u.bio ?? "");
      setLanguages((u.languages ?? []).filter((l): l is Language => (LANGUAGES as readonly string[]).includes(l)));
      setBranchStatus(u.branchStatus);
      setBranchPersonId(u.branchPersonId);
      setMotherName(u.motherName ?? "");
      setFatherName(u.fatherName ?? "");
      setKnownRelative(u.knownRelative ?? "");

      // Initialise the place field from existing data:
      //   - prefer the picked-place display name when coords are stored
      //   - else fall back to "city, country"
      if (u.placeLat != null && u.placeLng != null && u.placeDisplayName) {
        setPlaceText(u.placeDisplayName);
        setPlaceWasPicked(true);
      } else if (u.city || u.country) {
        setPlaceText(
          [u.city, u.country].filter((s): s is string => Boolean(s)).join(", "),
        );
      }
      setHydrated(true);
    }
  }, [meQuery.data, hydrated]);

  const save = useMutation<MeResponse, Error, UpdateProfileRequest>({
    mutationFn: (body) =>
      apiFetch<MeResponse>("/me", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData<MeResponse>(["me"], data);
      if (isOnboarding) {
        navigate("/", { replace: true });
      }
    },
  });

  // Notification preferences save on their own, not with the profile form.
  // The names go through firstName because the profile PATCH requires it.
  const prefs = useMutation<MeResponse, Error, Partial<Pick<UpdateProfileRequest, "notifyPosts" | "notifyNews" | "notifyEvents">>>({
    mutationFn: (body) =>
      apiFetch<MeResponse>("/me", {
        method: "PATCH",
        body: JSON.stringify({ firstName: meQuery.data?.user.firstName ?? firstName, ...body }),
      }),
    onSuccess: (data) => queryClient.setQueryData<MeResponse>(["me"], data),
  });

  if (meQuery.isLoading || !hydrated) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim()) return;

    // Build the place fields based on whether the user picked a suggestion
    // or just typed free text. Soft fallback: typed-only text goes into
    // city as a best guess (the World map will geocode it).
    let placeFields: Pick<
      UpdateProfileRequest,
      "city" | "country" | "placeDisplayName" | "placeLat" | "placeLng"
    >;
    if (pickedPlace) {
      placeFields = {
        city: pickedPlace.city ?? null,
        country: pickedPlace.country ?? null,
        placeDisplayName: pickedPlace.displayName,
        placeLat: pickedPlace.lat,
        placeLng: pickedPlace.lng,
      };
    } else if (placeWasPicked && placeText.trim()) {
      // User had a picked place but kept typing — keep the text but clear
      // the locked coordinates so the map re-geocodes if needed.
      placeFields = {
        city: placeText.trim(),
        country: null,
        placeDisplayName: null,
        placeLat: null,
        placeLng: null,
      };
    } else {
      // Free text — try to split a "City, Country" pattern, else dump the
      // whole string into city.
      const parts = placeText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      placeFields = {
        city: parts[0] || null,
        country: parts.length > 1 ? parts[parts.length - 1]! : null,
        placeDisplayName: null,
        placeLat: null,
        placeLng: null,
      };
    }

    save.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim() || null,
      bio: bio.trim() || null,
      languages,
      branchStatus,
      branchPersonId: branchStatus === "descendant" ? branchPersonId : null,
      motherName: motherName.trim() || null,
      fatherName: fatherName.trim() || null,
      knownRelative: knownRelative.trim() || null,
      ...placeFields,
    });
  }

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <div>
          <h1>{isOnboarding ? "Welcome — tell us about yourself" : "Profile"}</h1>
          {isOnboarding && (
            <p className="text-muted">
              A few details so other family members know who you are. You can
              update these any time from this page.
            </p>
          )}
        </div>

        <AvatarUploader
          me={meQuery.data!}
          onUpdated={(next) => queryClient.setQueryData<MeResponse>(["me"], next)}
        />

        {!isOnboarding && (
          <section className="card stack" style={{ padding: "14px 16px", gap: 8, maxWidth: 560 }}>
            <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Notifications on this device</h2>
            <p className="text-muted" style={{ margin: 0, fontSize: "0.9rem" }}>
              Get a notification and an app-icon badge when something happens
              in the family. Set per device — turn it on from each phone or
              computer you use.
            </p>
            <div style={{ maxWidth: 360 }}>
              <NotificationToggle />
            </div>
            <div className="stack" style={{ gap: 4, marginTop: 4 }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>Tell me about new…</span>
              {(
                [
                  ["notifyPosts", "Posts"],
                  ["notifyNews", "News"],
                  ["notifyEvents", "Events"],
                ] as const
              ).map(([key, label]) => (
                <label
                  key={key}
                  style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 400, margin: 0, fontSize: "0.9rem" }}
                >
                  <input
                    type="checkbox"
                    checked={meQuery.data?.user[key] ?? true}
                    disabled={prefs.isPending}
                    onChange={(e) => prefs.mutate({ [key]: e.target.checked })}
                  />
                  {label}
                </label>
              ))}
              <span className="text-subtle" style={{ fontSize: "0.8rem" }}>
                Comments on your posts and photos you're tagged in are always sent.
              </span>
            </div>
          </section>
        )}

        {primary && (
          <TreeSelfLink familySlug={primary.familySlug} me={meQuery.data!} />
        )}

        <form onSubmit={submit} className="stack" style={{ maxWidth: 560 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "var(--s-3)",
            }}
          >
            <div>
              <label htmlFor="first-name">First name *</label>
              <input
                id="first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                maxLength={120}
                autoFocus={isOnboarding}
              />
            </div>
            <div>
              <label htmlFor="last-name">Last name</label>
              <input
                id="last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={120}
              />
            </div>
          </div>

          {familySlug && (
            <div className="branch-hints stack" style={{ gap: "var(--s-3)" }}>
              <div>
                <h2 style={{ margin: 0 }}>
                  {isOnboarding
                    ? "Help us find you in the family tree"
                    : "Your place in the family tree"}
                </h2>
                <p className="text-muted" style={{ margin: "4px 0 0", fontSize: "0.9rem" }}>
                  {isOnboarding
                    ? "An admin checks every sign-up against the tree before approving you — without this there is nothing to match you against. Your branch and your parents' names are required; the last one is optional."
                    : "Helps admins link you to the right person in the tree. All optional."}
                </p>
              </div>

              {(branchesQuery.data?.branches.length ?? 0) > 0 && (
                <div>
                  <label>
                    Which branch of the{" "}
                    {branchesQuery.data?.founderName ?? "family"} family are you from?
                    {isOnboarding && " *"}
                  </label>
                  <div className="text-subtle" style={{ fontSize: "0.85rem", marginBottom: 8 }}>
                    Pick the child of {branchesQuery.data?.founderName ?? "the founder"} you
                    descend from.
                  </div>
                  <div className="branch-picker">
                    {branchesQuery.data!.branches.map((b) => {
                      const on = branchStatus === "descendant" && branchPersonId === b.id;
                      return (
                        <label key={b.id} className={`branch-option${on ? " is-on" : ""}`}>
                          <input
                            type="radio"
                            name="branch"
                            required={isOnboarding}
                            checked={on}
                            onChange={() => {
                              setBranchStatus("descendant");
                              setBranchPersonId(b.id);
                            }}
                          />
                          {b.name}
                          {b.birthYear && (
                            <span className="text-subtle" style={{ fontSize: "0.75rem" }}>
                              {b.birthYear}
                            </span>
                          )}
                        </label>
                      );
                    })}
                    {(
                      [
                        ["married_in", "I married into the family"],
                        ["not_sure", "Not sure"],
                      ] as const
                    ).map(([value, label]) => (
                      <label
                        key={value}
                        className={`branch-option branch-option--other${
                          branchStatus === value ? " is-on" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="branch"
                          required={isOnboarding}
                          checked={branchStatus === value}
                          onChange={() => {
                            setBranchStatus(value);
                            setBranchPersonId(null);
                          }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "var(--s-3)",
                }}
              >
                <div>
                  <label htmlFor="mother-name">
                    Mother's name{isOnboarding && !parentsUnknown && " *"}
                  </label>
                  <input
                    id="mother-name"
                    type="text"
                    value={motherName}
                    onChange={(e) => setMotherName(e.target.value)}
                    maxLength={160}
                    autoComplete="off"
                    required={isOnboarding && !parentsUnknown}
                    disabled={parentsUnknown}
                  />
                </div>
                <div>
                  <label htmlFor="father-name">
                    Father's name{isOnboarding && !parentsUnknown && " *"}
                  </label>
                  <input
                    id="father-name"
                    type="text"
                    value={fatherName}
                    onChange={(e) => setFatherName(e.target.value)}
                    maxLength={160}
                    autoComplete="off"
                    required={isOnboarding && !parentsUnknown}
                    disabled={parentsUnknown}
                  />
                </div>
              </div>
              {isOnboarding && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 400,
                    margin: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={parentsUnknown}
                    onChange={(e) => {
                      setParentsUnknown(e.target.checked);
                      if (e.target.checked) {
                        setMotherName("");
                        setFatherName("");
                      }
                    }}
                  />
                  I don't know these names
                </label>
              )}
              <div>
                <label htmlFor="known-relative">A relative who already uses the site</label>
                <input
                  id="known-relative"
                  type="text"
                  value={knownRelative}
                  onChange={(e) => setKnownRelative(e.target.value)}
                  maxLength={200}
                  placeholder="Their name, and how you're related"
                  autoComplete="off"
                />
                <div className="text-subtle" style={{ fontSize: "0.85rem", marginTop: 4 }}>
                  Anyone the admin can ask to vouch for you.
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="bio">Bio</label>
            <textarea
              id="bio"
              rows={4}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={2000}
              placeholder="A short note about you — work, hobbies, anything you want to share."
            />
          </div>

          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend style={{ fontWeight: 700, marginBottom: 6 }}>Languages you speak</legend>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {LANGUAGES.map((l) => (
                <label
                  key={l}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 14px",
                    border: "1px solid var(--c-border)",
                    borderRadius: 999,
                    minHeight: 44,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={languages.includes(l)}
                    onChange={(e) =>
                      setLanguages((prev) =>
                        e.target.checked ? [...prev, l] : prev.filter((x) => x !== l),
                      )
                    }
                    style={{ width: "auto", minHeight: 0, margin: 0 }}
                  />
                  {LANGUAGE_LABELS[l]}
                </label>
              ))}
            </div>
            <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: 4 }}>
              Used to pair you with relatives you can talk to in a live get-together.
            </p>
          </fieldset>

          <div>
            <label htmlFor="place">Where do you live?</label>
            <PlaceTypeahead
              value={placeText}
              onChange={(text) => {
                setPlaceText(text);
                if (pickedPlace) setPickedPlace(null);
              }}
              onPick={(place) => {
                setPickedPlace(place);
                setPlaceWasPicked(place !== null);
              }}
              hasPickedPlace={Boolean(pickedPlace) || placeWasPicked}
              placeholder="Start typing a city — pick from suggestions for accuracy"
            />
          </div>

          {save.error && <p className="text-error">{save.error.message}</p>}

          <div style={{ display: "flex", gap: "var(--s-3)" }}>
            <button
              type="submit"
              disabled={!firstName.trim() || save.isPending}
            >
              {save.isPending
                ? "Saving…"
                : isOnboarding
                  ? "Continue"
                  : "Save"}
            </button>
            {!isOnboarding && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate(-1)}
                disabled={save.isPending}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </main>
    </>
  );
}
